// Resumo da semana (0.17.0): os últimos 7 dias da empresa em poucas linhas —
// cargas e fretes, o que entrou, as despesas (diesel à parte), os vales — e a
// comparação com os 7 dias antes. Conta pura; a tela junta o que está
// atrasado e o que vence (A receber, A pagar, manutenção, custos).
import { creditosDaViagem, regraValendo } from './acerto.js';
import { avisosDeCustoEConsumo, textoDoAvisoDeCusto } from './avisos.js';
import { itensAPagar, separarAPagar, somarDias } from './despesas.js';
import { data, dinheiro, numeroBR } from './formato.js';
import { comContas } from './fretes.js';
import { avisosDaFrota } from './manutencao.js';
import { itensAReceber, separarAReceber } from './receber.js';

const centavos = (v) => Math.round((Number(v) || 0) * 100);
const vivo = (x) => !x.deleted_at;
const entre = (dia, de, ate) => Boolean(dia) && dia >= de && dia <= ate;

function numerosDoPeriodo({ fretes, lancamentos, viagens, categorias }, de, ate) {
  const viagensVivas = new Set(viagens.filter((v) => vivo(v) && v.status !== 'cancelada').map((v) => v.id));
  const grupo = new Map(categorias.map((c) => [c.id, c]));
  const r = { cargas: 0, fretes: 0, recebido: 0, despesas: 0, diesel: 0, litros: 0, manutencao: 0, outras: 0, vales: 0 };
  for (const bruto of fretes) {
    if (!vivo(bruto) || !viagensVivas.has(bruto.viagem_id)) continue;
    const f = comContas(bruto);
    if (entre(f.data_carregamento, de, ate)) {
      r.cargas += 1;
      r.fretes += centavos(f.valor_frete) + centavos(f.estadia_valor);
    }
    if (entre(f.adto_recebido_em, de, ate)) r.recebido += centavos(f.adto_valor);
    if (entre(f.saldo_recebido_em, de, ate)) r.recebido += centavos(f.saldo_valor);
  }
  for (const l of lancamentos) {
    if (!vivo(l) || l.origem === 'importacao' || !entre(l.data, de, ate)) continue;
    if (l.tipo === 'vale') r.vales += centavos(l.valor);
    if (l.tipo !== 'despesa') continue;
    const cat = grupo.get(l.categoria_id);
    r.despesas += centavos(l.valor);
    if (cat?.exige_hodometro) {
      r.diesel += centavos(l.valor);
      r.litros += Number(l.litros) || 0;
    } else if (cat?.grupo === 'manutencao') r.manutencao += centavos(l.valor);
    else r.outras += centavos(l.valor);
  }
  for (const k of ['fretes', 'recebido', 'despesas', 'diesel', 'manutencao', 'outras', 'vales']) r[k] /= 100;
  r.litros = Math.round(r.litros * 1000) / 1000;
  return r;
}

// `tabelas`: as de dadosDosRelatorios(). Os 7 dias até `hoje` e os 7 antes.
export function resumoDaSemana(tabelas, { hoje }) {
  const t = { fretes: [], lancamentos: [], viagens: [], categorias: [], ...tabelas };
  const de = somarDias(hoje, -6);
  const antesAte = somarDias(de, -1);
  const antesDe = somarDias(antesAte, -6);
  return {
    de,
    ate: hoje,
    ...numerosDoPeriodo(t, de, hoje),
    anterior: { de: antesDe, ate: antesAte, ...numerosDoPeriodo(t, antesDe, antesAte) },
  };
}

// Variação contra a semana anterior: "+12%", "−5%" ou null (sem base).
export function variacao(agora, antes) {
  if (!(antes > 0)) return null;
  const v = Math.round(((agora - antes) / antes) * 100);
  if (v === 0) return 'igual';
  return v > 0 ? `+${v}%` : `-${Math.abs(v)}%`;
}

// O resumo em texto, para mandar pelo WhatsApp (negrito com *). `atencao`:
// { atrasados: { qtd, valor }, pagar: { qtdVencidas, vencidas, qtdSemana, semana },
//   manutencao: { vencidas, perto }, custos: [linhas], acertos: viagens a acertar }
export function textoDoResumo(r, atencao, { empresa = 'HSA Frota' } = {}) {
  const comVariacao = (valor, antes) => {
    const v = variacao(valor, antes);
    return v && v !== 'igual' ? ` (${v} sobre a semana anterior)` : '';
  };
  const linhas = [
    `*${empresa} — resumo de ${data(r.de)} a ${data(r.ate)}*`,
    `Cargas: ${r.cargas} (${dinheiro(r.fretes)} em fretes${comVariacao(r.fretes, r.anterior.fretes)})`,
    `Recebido dos clientes: ${dinheiro(r.recebido)}`,
    `Despesas: ${dinheiro(r.despesas)}${comVariacao(r.despesas, r.anterior.despesas)}`,
  ];
  const partes = [];
  if (r.diesel > 0) partes.push(`diesel ${dinheiro(r.diesel)}${r.litros > 0 ? `, ${numeroBR(Math.round(r.litros))} L` : ''}`);
  if (r.manutencao > 0) partes.push(`manutenção ${dinheiro(r.manutencao)}`);
  if (r.outras > 0) partes.push(`outras ${dinheiro(r.outras)}`);
  if (partes.length > 0) linhas.push(`  ${partes.join(' · ')}`);
  if (r.vales > 0) linhas.push(`Vales aos motoristas: ${dinheiro(r.vales)}`);
  const pede = pedeAtencao(atencao);
  if (pede.length > 0) linhas.push('', '*Pede atenção*', ...pede.map((p) => `• ${p}`));
  return linhas.join('\n');
}

// O que pede atenção, uma linha por assunto (a tela e o texto do WhatsApp).
export function pedeAtencao(atencao) {
  const pede = [];
  if (atencao.atrasados?.qtd > 0) pede.push(`A receber atrasado: ${dinheiro(atencao.atrasados.valor)} (${atencao.atrasados.qtd})`);
  if (atencao.pagar?.qtdVencidas > 0) pede.push(`Contas vencidas: ${dinheiro(atencao.pagar.vencidas)} (${atencao.pagar.qtdVencidas})`);
  if (atencao.pagar?.qtdSemana > 0) pede.push(`A pagar nos próximos 7 dias: ${dinheiro(atencao.pagar.semana)}`);
  if (atencao.manutencao?.vencidas > 0) pede.push(`Manutenção vencida: ${atencao.manutencao.vencidas} ${atencao.manutencao.vencidas === 1 ? 'item' : 'itens'}`);
  if (atencao.manutencao?.perto > 0) pede.push(`Manutenção perto de vencer: ${atencao.manutencao.perto} ${atencao.manutencao.perto === 1 ? 'item' : 'itens'}`);
  for (const c of atencao.custos ?? []) pede.push(c);
  if (atencao.acertos > 0) pede.push(`Acertos a fazer: ${atencao.acertos} ${atencao.acertos === 1 ? 'viagem encerrada' : 'viagens encerradas'}`);
  return pede;
}

// O que pede atenção na empresa agora (a tela do resumo e o e-mail de
// segunda usam a mesma conta). `tabelas`: fretes, clientes, lancamentos,
// parcelas, acertos, colaboradores, veiculos, itens (do plano de manutenção),
// viagens, licencas, categorias e regras (de remuneração).
export function atencaoDaEmpresa(tabelas, { hoje }) {
  const vivo = (x) => !x.deleted_at;
  const t = { fretes: [], clientes: [], lancamentos: [], veiculos: [], itens: [], viagens: [], ...tabelas };
  const receber = separarAReceber(itensAReceber(t.fretes, t.clientes), hoje);
  const pagar = separarAPagar(itensAPagar(t), hoje);
  const tratores = t.veiculos.filter((v) => vivo(v) && v.tipo === 'trator');
  const manutencao = avisosDaFrota({ veiculos: tratores, itens: t.itens.filter(vivo), leituras: t.lancamentos.filter(vivo), hoje });
  const custos = avisosDeCustoEConsumo(t, { hoje, regraValendo, creditosDaViagem });
  return {
    atrasados: { qtd: receber.atrasados.length, valor: receber.totais.atrasados },
    pagar: { qtdVencidas: pagar.vencidas.length, vencidas: pagar.totais.vencidas, qtdSemana: pagar.semana.length, semana: pagar.totais.semana },
    manutencao: {
      vencidas: manutencao.filter((a) => a.situacao.estado === 'vencida').length,
      perto: manutencao.filter((a) => a.situacao.estado !== 'vencida').length,
    },
    custos: custos.map(textoDoAvisoDeCusto),
    acertos: t.viagens.filter((v) => vivo(v) && v.status === 'encerrada' && !v.acerto_id).length,
  };
}

// A semana teve movimento ou algo pede atenção? (sem nada, não manda e-mail)
export function temNovidade(r, atencao) {
  return r.cargas > 0 || r.despesas > 0 || r.recebido > 0 || r.vales > 0 || pedeAtencao(atencao).length > 0;
}
